import includes from 'lodash/includes';
import { fg } from '@atlassian/jira-feature-gating';
import {
	isIssueEntryIssue,
	type IssueId,
	type IssueEntry,
} from '@atlassian/jira-software-board-common';
import type { RowState } from '@atlassian/jira-software-fast-virtual/src/common/types';
import { createStore, createHook, type Action } from '@atlassian/react-sweet-state';

type ColumnPosition = { x: number; width: number };

type State = {
	issuesWithIssueLinkPositionMap: Map<string, RowState>;
	swimlanePositionMap: Map<string, RowState>;
	columnPositionMap: Map<number, ColumnPosition>;
};
type Actions = typeof actions;

const initialState: State = {
	issuesWithIssueLinkPositionMap: new Map(),
	swimlanePositionMap: new Map(),
	columnPositionMap: new Map(),
};

const actions = {
	updateIssuesWithIssueLinkPositions:
		(
			issueEntries: IssueEntry[],
			issuesWithIssueLinksIds: Array<IssueId>,
			issuePositions?: RowState[],
			isUnscheduledWorkColumnPanel?: boolean,
		): Action<State> =>
		({ setState, getState }) => {
			if (
				!isUnscheduledWorkColumnPanel &&
				issuePositions &&
				fg('dependency_visualisation_program_board_fe_and_be')
			) {
				const issueIdsWithLinks = issueEntries.reduce<string[]>((acc, issueEntry) => {
					if (
						isIssueEntryIssue(issueEntry) &&
						includes(issuesWithIssueLinksIds, issueEntry.issueId)
					) {
						acc.push(String(issueEntry.issueId));
					}
					return acc;
				}, []);

				const { issuesWithIssueLinkPositionMap } = getState();

				const newIssueWithIssueLinkPositionMap = new Map(issuesWithIssueLinkPositionMap);

				issueIdsWithLinks.forEach((issueId, index) => {
					const issuePosition = issuePositions[index];
					if (issuePosition) {
						newIssueWithIssueLinkPositionMap.set(issueId, issuePosition);
					}
				});

				setState({ issuesWithIssueLinkPositionMap: newIssueWithIssueLinkPositionMap });
			}
		},

	updateSwimlanePositions:
		(swimlanePositions: RowState[], swimlaneIds: string[]): Action<State> =>
		({ setState, getState }) => {
			const { swimlanePositionMap } = getState();

			const newSwimlanePositionMap = new Map(swimlanePositionMap);

			swimlaneIds.forEach((swimlaneId, index) => {
				const swimlanePosition = swimlanePositions[index];
				if (swimlaneId && swimlanePosition) {
					newSwimlanePositionMap.set(swimlaneId, swimlanePosition);
				}
			});

			setState({ swimlanePositionMap: newSwimlanePositionMap });
		},

	updateColumnPositionMap:
		(columnPosition: ColumnPosition, columnId: number): Action<State> =>
		({ setState, getState }) => {
			const { columnPositionMap } = getState();

			const newColumnPositionMap = new Map(columnPositionMap);

			newColumnPositionMap.set(columnId, columnPosition);

			setState({ columnPositionMap: newColumnPositionMap });
		},
};

const Store = createStore<State, Actions>({
	name: 'dependencyLineStore',
	initialState,
	actions,
});

export const useDependencyLinks = createHook(Store);
